/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDate } from '@fullcalendar/core/index.js'
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { twoDigits } from '../../../assets/ts/formatting/formatting'
import { displayAriaLabel } from '../../../assets/ts/general/general'
import Buttons from '../../../components/buttons/buttons'
import PageOverlayBusy from '../../../components/page-overlay/page-overlay-busy'
import usePascodeMetadata from '../../../hooks/use-pascode-metadata'
import { SettingsHeader } from '../../../layouts/header/settings-header'
import { MetadataContext } from '../../../store/metadata-context-provider'
import {
  ALL_SELECT_OPTION,
  ALL_SELECT_OPTION_STRING,
} from '../../../utils/constants/global-constants'
import NO_RESULTS from '../../../utils/constants/no-results'
import CustomSelect from '../../../utils/controls/custom-select'
import InputCheckbox from '../../../utils/controls/input-checkbox'
import InputSearch from '../../../utils/controls/input-search'
import { ServicedBy } from '../../../utils/controls/select-options-data'
import AddEditView from './add-edit-view'

const PascodeConfiguration = (props: any) => {
  const { components, majcoms } = useContext(MetadataContext)

  const { fetchPascodeMetadata, pascodeMetadata, pascodeMetadataIsLoading } = usePascodeMetadata({
    IsArchived: null,
    isAscending: false,
  })
  const [inputValues, setInputValues] = useState({
    assignedComponentCode: -1,
    installation: '',
    search: '',
    servicedBy: '',
    servicingMAJCOM: '',
    servicingOrganization: '',
  })

  const [assignedComponentOptions, setAssignedComponentOptions] = useState<any[]>([])
  const [majcomOptions, setMajcomOptions] = useState<any[]>([])
  const [servicingOrganizationOptions, setServicingOrganizationOptions] = useState<any[]>([])
  const [assignedLocationOptions, setAssignedLocationOptions] = useState<any[]>([])

  const [searchedPascodeMetadata, setSearchedPascodeMetadata] = useState<any[]>([])

  const initialAssignedComponentOptions = useMemo(() => {
    const assignedCompOptions: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry) => pascodeEntry.AssignedComponentCode !== null)
        .map((pascodeEntry) => ({
          label: pascodeEntry['AssignedComponent/MetadataValue'],
          value: pascodeEntry.AssignedComponentCode,
        })),
    ]
    const formattedAssignedComp = [
      ALL_SELECT_OPTION,
      ...Object.values(
        assignedCompOptions.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    setAssignedComponentOptions(formattedAssignedComp)
    return formattedAssignedComp
  }, [pascodeMetadata])

  const initialMajcomOptions = useMemo(() => {
    const majOptions: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry) => pascodeEntry.ServicingMAJCOM !== null)
        .map((pascodeEntry) => ({
          label: pascodeEntry.ServicingMAJCOM,
          value: pascodeEntry.ServicingMAJCOM,
        })),
    ]

    const formattedMajcom = [
      ALL_SELECT_OPTION_STRING,
      ...Object.values(
        majOptions.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    setMajcomOptions(formattedMajcom)
    return formattedMajcom
  }, [pascodeMetadata])
  const initialServicingOrganizationOption = useMemo(() => {
    const servOrgOptions: any[] = [
      ...pascodeMetadata
        .map((pascodeEntry) => ({
          label: pascodeEntry.ServicingOrganization,
          value: pascodeEntry.ServicingOrganization,
        })),
    ]

    const formattedServicingOrg = [
      ALL_SELECT_OPTION_STRING,
      ...Object.values(
        servOrgOptions.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]
    setServicingOrganizationOptions(formattedServicingOrg)
    return formattedServicingOrg
  }, [pascodeMetadata])

  const initialAssignedLocationOptions = useMemo(() => {
    const majOptions: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry) => pascodeEntry.Installation !== null)
        .map((pascodeEntry) => ({
          label: pascodeEntry.Installation,
          value: pascodeEntry.Installation,
        })),
    ]

    const formattedInstallation = [
      ALL_SELECT_OPTION_STRING,
      ...Object.values(
        majOptions.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    setAssignedLocationOptions(formattedInstallation)
    return formattedInstallation
  }, [pascodeMetadata])

  const onServiceByChange = (e: { label: string; value: string }) => {
    const assignedComponentOptionsArr: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry: any) => e.value === '' || pascodeEntry.ServicingBy === e.value)
        .map((pascodeEntry: any) => ({
          label: pascodeEntry['AssignedComponent/MetadataValue'],
          value: pascodeEntry.AssignedComponentCode,
        })),
    ]

    const assignedComponentOptions = [
      ...Object.values(
        assignedComponentOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const acFilteredOpt = assignedComponentOptions.filter((opt) => opt.label !== null)

    if (acFilteredOpt.length > 1 || e.value === '') acFilteredOpt.unshift(ALL_SELECT_OPTION)

    setAssignedComponentOptions(acFilteredOpt)

    const optionsArr: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry: any) => e.value === '' || pascodeEntry.ServicingBy === e.value)
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.ServicingMAJCOM,
          value: pascodeEntry.ServicingMAJCOM,
        })),
    ]

    const options = [
      ...Object.values(
        optionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredOpt = options.filter((opt) => opt.label !== null)

    if (filteredOpt.length > 1 || e.value === '') filteredOpt.unshift(ALL_SELECT_OPTION_STRING)

    setMajcomOptions(filteredOpt)

    const servicingOrganizationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry: any) => e.value === '' || pascodeEntry.ServicingBy === e.value)
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.ServicingOrganization,
          value: pascodeEntry.ServicingOrganization,
        })),
    ]
    const servicingOrganizationOptions = [
      ...Object.values(
        servicingOrganizationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]
    if (servicingOrganizationOptions.length > 1 || e.value === '')
      servicingOrganizationOptions.unshift(ALL_SELECT_OPTION_STRING)

    setServicingOrganizationOptions(servicingOrganizationOptions)

    const installationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter((pascodeEntry: any) => e.value === '' || pascodeEntry.ServicingBy === e.value)
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.Installation,
          value: pascodeEntry.Installation,
        })),
    ]

    const assignedLocationOptions = [
      ...Object.values(
        installationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredInstallationOpt = assignedLocationOptions.filter((opt) => opt.label !== null)

    if (filteredInstallationOpt.length > 1 || e.value === '')
      filteredInstallationOpt.unshift(ALL_SELECT_OPTION_STRING)

    setAssignedLocationOptions(filteredInstallationOpt)

    setInputValues((prev) => ({
      ...prev,
      assignedComponentCode:
        assignedComponentOptions.length === 1 ? assignedComponentOptions[0].value : -1,
      installation: assignedLocationOptions.length === 1 ? assignedLocationOptions[0].value : '',
      servicedBy: e.value,
      servicingMAJCOM: options.length === 1 ? options[0].value : '',
      servicingOrganization:
        servicingOrganizationOptions.length === 1 ? servicingOrganizationOptions[0].value : '',
    }))
  }
  const onAssignComponentChange = (e: { label: string; value: number }) => {
    const optionsArr: any[] = [
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === -1 || pascodeEntry.AssignedComponentCode === e.value) &&
            (inputValues.servicedBy === '' || pascodeEntry.ServicingBy === inputValues.servicedBy)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.ServicingMAJCOM,
          value: pascodeEntry.ServicingMAJCOM,
        })),
    ]

    const options = [
      ...Object.values(
        optionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredOpt = options.filter((opt) => opt.label !== null)

    if (filteredOpt.length > 1 || e.value === -1) filteredOpt.unshift(ALL_SELECT_OPTION_STRING)

    setMajcomOptions(filteredOpt)

    const servicingOrganizationOptionsArr: any[] = [  
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === -1 || pascodeEntry.AssignedComponentCode === e.value) &&
            (inputValues.servicedBy === '' || pascodeEntry.ServicingBy === inputValues.servicedBy)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.ServicingOrganization,
          value: pascodeEntry.ServicingOrganization,
        })),
    ]
    const servicingOrganizationOptions = [
      ...Object.values(
        servicingOrganizationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]
    if (servicingOrganizationOptions.length > 1 || e.value === -1)
      servicingOrganizationOptions.unshift(ALL_SELECT_OPTION_STRING)

    setServicingOrganizationOptions(servicingOrganizationOptions)

    const installationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === -1 || pascodeEntry.AssignedComponentCode === e.value) &&
            (inputValues.servicedBy === '' || pascodeEntry.ServicingBy === inputValues.servicedBy)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.Installation,
          value: pascodeEntry.Installation,
        })),
    ]

    const assignedLocationOptions = [
      ...Object.values(
        installationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredInstallationOpt = assignedLocationOptions.filter((opt) => opt.label !== null)

    if (filteredInstallationOpt.length > 1 || e.value === -1)
      filteredInstallationOpt.unshift(ALL_SELECT_OPTION_STRING)

    setAssignedLocationOptions(filteredInstallationOpt)

    setInputValues((prev) => ({
      ...prev,
      assignedComponentCode: e.value,
      installation: assignedLocationOptions.length === 1 ? assignedLocationOptions[0].value : '',
      servicingMAJCOM: options.length === 1 ? options[0].value : '',
      servicingOrganization:
        servicingOrganizationOptions.length === 1 ? servicingOrganizationOptions[0].value : '',
    }))
  }
  const onServicingMAJCOMChange = (e: { label: string; value: string }) => {
    const servicingOrganizationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === '' || pascodeEntry.ServicingMAJCOM === e.value) &&
            (inputValues.servicedBy === '' ||
              pascodeEntry.ServicingBy === inputValues.servicedBy) &&
            (inputValues.assignedComponentCode === -1 ||
              pascodeEntry.AssignedComponentCode === inputValues.assignedComponentCode)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.ServicingOrganization,
          value: pascodeEntry.ServicingOrganization,
        })),
    ]
    const servicingOrganizationOptions = [
      ...Object.values(
        servicingOrganizationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]
    if (servicingOrganizationOptions.length > 1 || e.value === '')
      servicingOrganizationOptions.unshift(ALL_SELECT_OPTION_STRING)

    setServicingOrganizationOptions(servicingOrganizationOptions)
    
    const installationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === '' || pascodeEntry.ServicingMAJCOM === e.value) &&
            (inputValues.servicedBy === '' ||
              pascodeEntry.ServicingBy === inputValues.servicedBy) &&
            (inputValues.assignedComponentCode === -1 ||
              pascodeEntry.AssignedComponentCode === inputValues.assignedComponentCode)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.Installation,
          value: pascodeEntry.Installation,
        })),
    ]

    const assignedLocationOptions = [
      ...Object.values(
        installationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredInstallationOpt = assignedLocationOptions.filter((opt) => opt.label !== null)

    if (filteredInstallationOpt.length > 1 || e.value === '')
      filteredInstallationOpt.unshift(ALL_SELECT_OPTION_STRING)

    setAssignedLocationOptions(filteredInstallationOpt)

    setInputValues((prev) => ({
      ...prev,
      installation: assignedLocationOptions.length === 1 ? assignedLocationOptions[0].value : '',
      servicingMAJCOM: e.value,
      servicingOrganization:
        servicingOrganizationOptions.length === 1 ? servicingOrganizationOptions[0].value : '',
    }))
  }

  const onAssignedLocationChange = (e: { label: string; value: string }) => {
    setInputValues((prev) => ({
      ...prev,
      installation: e.value,
    }))
  }
  const onServicingOrganizationChange = (e: { label: string; value: string }) => {
    const installationOptionsArr: any[] = [
      ...pascodeMetadata
        .filter(
          (pascodeEntry: any) =>
            (e.value === '' || pascodeEntry.ServicingOrganization === e.value) &&
            (inputValues.servicedBy === '' ||
              pascodeEntry.ServicingBy === inputValues.servicedBy) &&
            (inputValues.assignedComponentCode === -1 ||
              pascodeEntry.AssignedComponentCode === inputValues.assignedComponentCode)
        )
        .map((pascodeEntry: any) => ({
          label: pascodeEntry.Installation,
          value: pascodeEntry.Installation,
        })),
    ]

    const assignedLocationOptions = [
      ...Object.values(
        installationOptionsArr.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.value]: obj,
          }),
          {}
        )
      ),
    ]

    const filteredInstallationOpt = assignedLocationOptions.filter((opt) => opt.label !== null)

    if (filteredInstallationOpt.length > 1 || e.value === '')
      filteredInstallationOpt.unshift(ALL_SELECT_OPTION_STRING)

    setAssignedLocationOptions(filteredInstallationOpt)
    setInputValues((prev) => ({
      ...prev,
      installation: assignedLocationOptions.length === 1 ? assignedLocationOptions[0].value : '',
      servicingOrganization: e.value,
    }))
  }

  function Row(pascodes: any) {
    return pascodes.map((pascode: any) => (
      <>
        <tr>
          <td aria-label={`${pascode.ServicingBy} Support`} tabIndex={0}>
            {`${pascode.ServicingBy} Support`}
          </td>
          <td aria-label={pascode['AssignedComponent/MetadataValue']} tabIndex={0}>
            {pascode['AssignedComponent/MetadataValue']}
          </td>
          <td aria-label={pascode.ServicingMAJCOM} tabIndex={0}>
            {pascode.ServicingMAJCOM}
          </td>
          <td aria-label={pascode.ServicingOrganization} tabIndex={0}>
            {pascode.ServicingOrganization}
          </td>
          <td aria-label={pascode.Installation} tabIndex={0}>
            {pascode.Installation}
          </td>
          <td aria-label={pascode.Organization} tabIndex={0}>
            {pascode.Organization}
          </td>
          <td aria-label={pascode.PASCODE} tabIndex={0}>
            {pascode.PASCODE}
          </td>
          <td aria-label={pascode.AdditionalPASCODEsSupported} tabIndex={0}>
            {pascode.AdditionalPASCODEsSupported}
          </td>
          <td aria-label={pascode.ServicingCSSPASCODE} tabIndex={0}>
            {pascode.ServicingCSSPASCODE}
          </td>
          <td
            aria-label={displayAriaLabel(
              pascode.ItemModifiedBy.properties.firstName,
              pascode.ItemModifiedBy.properties.lastName,
              pascode.ItemModified
            )}
            tabIndex={0}
          >
            {pascode.ItemModifiedBy.properties.firstName}{' '}
            {pascode.ItemModifiedBy.properties.lastName} | {formatDate(pascode.ItemModified)}
          </td>
          <td aria-label={pascode.IsArchived ? 'Yes' : 'No'} tabIndex={0}>
            {pascode.IsArchived ? 'Yes' : 'No'}
          </td>
          <td tabIndex={0}>
            <Buttons
              aria-label='Edit'
              className='btn-border btn-sm font-0 font-xl-14 btn-border-radius3 ms-auto color-primary text-nowrap sourcesansprosemibold'
              icon='icon-pencil me-xl-1 font-10'
              label='Edit'
              onClick={() => {
                setShowEdit(true)
                onEdit({ ItemKey: pascode.PASCODE })
                setShowAddPopup(false)
              }}
            />
          </td>
        </tr>
        {showEdit && inEditMode.rowKey === pascode.PASCODE ? (
          <tr>
            <td colSpan={9}>
              <div className='mx-2 border-radius whitebg border-primary mt-3' id='scroll'>
                <div className='px-2 py-1'>
                  <AddEditView
                    components={components}
                    fetchPascodeMetadata={fetchPascodeMetadata}
                    filtersvalue={addEditValue}
                    majcoms={majcoms}
                    pascode={pascode}
                    pascodeMetadata={pascodeMetadata}
                    setShowEdit={setShowEdit}
                    showAddPopup={showAddPopup}
                    showEdit={showEdit}
                  />
                </div>
              </div>
            </td>
          </tr>
        ) : (
          ''
        )}
      </>
    ))
  }

  function handleSelectChange(event: any) {
    if (event.target.value === '') {
      setSearchedPascodeMetadata(pascodeMetadata[0])
    } else {
      const searchData: any = []
      const departmentfilter = pascodeMetadata.filter((emp: any) => {
        return emp.department === event.target.value
      })
      searchData.push(departmentfilter)
      setSearchedPascodeMetadata(searchData)
    }
  }

  function handleInputChange(event: any) {
    const searchText = event.target.value.toLowerCase()
    const searchData: any = []
    const statusfilter = pascodeMetadata.filter((emp: any) => {
      return emp.status.toLowerCase().indexOf(searchText) > -1
    })
    searchData.push(statusfilter)
    setSearchedPascodeMetadata(searchData)
  }
  const rows: any = []
  const [showEdit, setShowEdit] = useState(false)
  const [showAddPopup, setShowAddPopup] = useState(false)
  const [inEditMode, setInEditMode] = useState({
    rowKey: null,
    status: false,
  })
  function addEditValue(showAddPopup: any) {
    setShowAddPopup(showAddPopup)
  }
  /* Function call on clicking Add button */
  const onClickADD = () => {
    setShowAddPopup(!showAddPopup)
    setInEditMode({
      rowKey: null,
      status: false,
    })
  }
  /* Function call on edit */
  const onEdit = (item: any) => {
    setInEditMode({
      rowKey: item.ItemKey,
      status: true,
    })
  }
  /* Function call on cancel */
  const onCancel = () => {
    setShowEdit(false)
    setInEditMode({
      rowKey: null,
      status: false,
    })
  }
  function onClearAll() {
    fetchPascodeMetadata()
    setInputValues({
      assignedComponentCode: -1,
      installation: '',
      search: '',
      servicedBy: '',
      servicingMAJCOM: '',
      servicingOrganization: '',
    })
  }

  const onChangeInput = (label: any, value: any) => {
    setInputValues({
      ...inputValues,
      [label]: value,
    })
  }
  useEffect(() => {
    setSearchedPascodeMetadata(pascodeMetadata)
  }, [pascodeMetadata])

  useEffect(() => {
    if (showEdit === false) {
      onCancel()
    }
  }, [showEdit])

  useEffect(() => {
    const filteredPascodes = pascodeMetadata.filter(
      (pascode) =>
        (inputValues.installation === '' || pascode.Installation === inputValues.installation) &&
        (inputValues.servicedBy === '' || pascode.ServicingBy === inputValues.servicedBy) &&
        (inputValues.servicingMAJCOM === '' ||
          pascode.ServicingMAJCOM === inputValues.servicingMAJCOM) &&
        (inputValues.assignedComponentCode === -1 ||
          pascode.AssignedComponentCode === inputValues.assignedComponentCode) &&
        (inputValues.servicingOrganization === '' || pascode.ServicingOrganization === inputValues.servicingOrganization) &&
        (inputValues.search === '' ||
          pascode.PASCODE.toLowerCase().includes(inputValues.search.toLowerCase()) ||
          pascode.Installation.toLowerCase().includes(inputValues.search.toLowerCase()) ||
          pascode.Organization.toLowerCase().includes(inputValues.search.toLowerCase()) ||
          pascode['AssignedComponent/MetadataValue']
            .toLowerCase()
            .includes(inputValues.search.toLowerCase()) ||
          pascode.ServicingMAJCOM.toLowerCase().includes(inputValues.search.toLowerCase()))
    )
    setSearchedPascodeMetadata(filteredPascodes)
  }, [inputValues])

  return (
    <div className='container'>
      {pascodeMetadataIsLoading && <PageOverlayBusy />}
      <div className='row'>
        <div className='shadow card mt-0 p-0 tabscontent-height overflow-hidden'>
          <div className='d-flex w-100 p-relative align-items-center p-2 flex-wrap bgcolor-38 justify-content-between '>
            <h2
              aria-label='PASCODE Configuration'
              className='d-flex align-items-center whitetext poppins-medium font-14 me-1 text-uppercase'
              tabIndex={0}
            >
              PASCODE Configuration
            </h2>
            <div className='d-flex align-items-center py-1 flex-wrap'>
              <div className='mx-2 pascodeselectdropdown'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100 sourcesanspro font-13 customselect'
                  formClassName='form-horizontal text-nowrap dash poppins-semibold font-11 whitetext'
                  id='ServicedBy'
                  label='Serviced By'
                  name='ServicedBy'
                  onChange={onServiceByChange}
                  options={ServicedBy}
                  value={inputValues.servicedBy}
                />
              </div>
              <div className='mx-2 pascodeselectdropdown'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100 sourcesanspro font-13 customselect'
                  formClassName='form-horizontal text-nowrap dash poppins-semibold font-11 whitetext'
                  id='Assigned Component'
                  label='Assigned Component'
                  name='Assigned Component'
                  onChange={onAssignComponentChange}
                  options={assignedComponentOptions}
                  value={inputValues.assignedComponentCode}
                />
              </div>
              <div className='mx-2 pascodeselectdropdown'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100 sourcesanspro font-13 customselect'
                  formClassName='form-horizontal text-nowrap dash poppins-semibold font-11 whitetext'
                  id='Servicing MAJCOM'
                  label='Servicing MAJCOM'
                  name='Servicing MAJCOM'
                  onChange={onServicingMAJCOMChange}
                  options={majcomOptions}
                  value={inputValues.servicingMAJCOM}
                />
              </div>
              <div className='mx-2 pascodeselectdropdown'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100 sourcesanspro font-13 customselect'
                  formClassName='form-horizontal text-nowrap dash poppins-semibold font-11 whitetext'
                  id='Servicing Organization'
                  label='Servicing Organization'
                  name='Servicing Organization'
                  onChange={onServicingOrganizationChange}
                  options={servicingOrganizationOptions}
                  value={inputValues.servicingOrganization}
                />
              </div>
              <div className='mx-2 pascodeselectdropdown'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100 sourcesanspro font-13 customselect'
                  formClassName='form-horizontal text-nowrap dash poppins-semibold font-11 whitetext'
                  id='Assigned Location'
                  label='Assigned Location'
                  name='Assigned Location'
                  onChange={onAssignedLocationChange}
                  options={assignedLocationOptions}
                  value={inputValues.installation}
                />
              </div>
              <div className='dividerdashed vertical mx-1 mx-lg-3 d-none d-md-block'></div>
              <div className='mx-2 ms-auto pt-1'>
                <Buttons
                  aria-label='ADD'
                  className='btn-sm font-0 font-xl-13 btn-primary ms-auto whitetext text-nowrap border-radius poppins-bold'
                  icon='icon-add me-xl-1 font-11'
                  label='ADD'
                  onClick={() => {
                    setShowAddPopup(!showAddPopup)
                    onCancel()
                  }}
                />
              </div>
            </div>
          </div>
          <div className='p-0 settings-main px-2'>
            {showAddPopup && (
              <AddEditView
                components={components}
                fetchPascodeMetadata={fetchPascodeMetadata}
                filtersvalue={addEditValue}
                majcoms={majcoms}
                pascodeMetadata={pascodeMetadata}
                setShowEdit={setShowEdit}
                showAddPopup={showAddPopup}
                showEdit={showEdit}
              />
            )}
          </div>
          <div className='p-3'>
            <SettingsHeader
              count={twoDigits(searchedPascodeMetadata.length)}
              extras={
                <div className='d-flex align-items-center flex-wrap settingsfilters'>
                  <div className='mx-2 my-sm-2'>
                    <InputSearch
                      aria-label='Assigned Location Organization or Passcode'
                      className='border w-100 form-lg border-radius03  font-13 sourcesanspro max-w-250 min-w-250 maxfitcontent'
                      formClassName=' form-vertical font-12 poppins-medium'
                      icon='icon-search pe-2 title-color'
                      inputProps={{
                        className: 'font-13 poppins-medium title-color',
                        id: 'Profilesearch',
                        maxLength: '500',
                        name: 'Profile search',
                        placeholder: 'Assigned Location, Organization, or PASCODE',
                        value: inputValues.search,
                      }}
                      onChange={(e) => onChangeInput('search', e.target.value)}
                      searchgroup=' w-100 d-flex align-items-center inputswithicon'
                    />
                  </div>
                  <Buttons
                    className='ms-auto btn-sm d-flex align-items-center links links-over-underline'
                    label='Clear All'
                    onClick={() => {
                      onClearAll()
                    }}
                    type='button'
                  />
                  {/* <div className='dividerdashed vertical mx-1 mx-lg-3 d-none d-md-block'></div>
                  <div className='mx-2 my-sm-2 d-none'>
                    <Buttons
                      aria-label='Edit'
                      className='btn-sm bgcolor-39 ms-auto font-0 font-xl-13 whitetext text-nowrap border-radius poppins-medium'
                      icon='icon-pencil me-xl-1 font-11'
                      label='Edit'
                    />
                  </div>
                  <div className='mx-2 d-none'>
                    <Buttons
                      aria-label='Save'
                      className='btn-sm bgcolor-39 font-0 font-xl-13 ms-auto whitetext text-nowrap border-radius poppins-medium'
                      icon='icon-save me-xl-1 font-11'
                      label='Save'
                    />
                  </div>
                  <div className='mx-2 d-none'>
                    <Buttons
                      aria-label='Cancel'
                      className='btn-sm btn-border-radius3 font-0 font-xl-13 btn-border1 ms-auto title-color2 text-nowrap border-radius poppins-medium'
                      icon='icon-close me-xl-1 font-11 color-primary'
                      label='Cancel'
                    />
                  </div> */}
                </div>
              }
              name='PASCODE'
            />
            <div className='w-100 overflow-auto px-1 pascodetable'>
              <table className='table-borderless w-100 sticky-top second-column last-column table-rowgap'>
                <thead>
                  <tr>
                    <th aria-label='Serviced By' tabIndex={0}>
                      Serviced By
                    </th>
                    <th aria-label='Assigned Component' tabIndex={0}>
                      Assigned Component
                    </th>
                    <th aria-label='Servicing MAJCOM' tabIndex={0}>
                      Servicing MAJCOM
                    </th>
                    <th aria-label='Servicing Organization' tabIndex={0}>
                      Servicing Organization
                    </th>
                    <th aria-label='Assigned Location' tabIndex={0}>
                      Assigned Location
                    </th>
                    <th aria-label='Organization' tabIndex={0}>
                      Organization
                    </th>
                    <th aria-label='PASCODE' tabIndex={0}>
                      PASCODE
                    </th>
                    <th aria-label='Additional PASCODE’s Supported' tabIndex={0}>
                      Additional PASCODE’s Supported
                    </th>
                    <th aria-label='Servicing CSS PASCODE if different' tabIndex={0}>
                      Servicing CSS PASCODE if different
                    </th>
                    <th aria-label='Modified' tabIndex={0}>
                      Modified
                    </th>
                    <th aria-label='Is Archived' tabIndex={0}>
                      Is Archived
                    </th>
                    <th aria-label='Actions' tabIndex={0}>
                      Actions
                    </th>
                  </tr>
                </thead>
                {searchedPascodeMetadata.length > 0 && (
                  <tbody>{Row(searchedPascodeMetadata)}</tbody>
                )}
              </table>
              {searchedPascodeMetadata.length <= 0 && (
                 <div>
                 <NO_RESULTS/>
                 </div>
              )}
            </div>
            <div className='w-100 overflow-auto px-1 pascodetable d-none'>
              <table className='table-borderless w-100 sticky-top second-column last-column'>
                <thead>
                  <tr>
                    <th>
                      <InputCheckbox
                        formClassName='form-horizontal font-0'
                        inputProps={{
                          id: 'all',
                          label: 'All',
                          name: 'Input Check',
                          type: 'checkbox',
                        }}
                      />
                    </th>
                    <th aria-label='Serviced By' tabIndex={0}>
                      Serviced By
                    </th>
                    <th aria-label='Assigned Component' tabIndex={0}>
                      Assigned Component
                    </th>
                    <th aria-label='Servicing MAJCOM' tabIndex={0}>
                      Servicing MAJCOM
                    </th>
                    <th aria-label='Assigned Location' tabIndex={0}>
                      Assigned Location
                    </th>
                    <th aria-label='Organization' tabIndex={0}>
                      Organization
                    </th>
                    <th aria-label='PASCODE' tabIndex={0}>
                      PASCODE
                    </th>
                    <th aria-label='Additional PASCODE’s Supported' tabIndex={0}>
                      Additional PASCODE’s Supported
                    </th>
                    <th aria-label='Servicing CSS PASCODE if different' tabIndex={0}>
                      Servicing CSS PASCODE if different
                    </th>
                    <th aria-label='Modified' tabIndex={0}>
                      Modified
                    </th>
                    <th aria-label='Is Archived' tabIndex={0}>
                      Is Archived
                    </th>
                    <th aria-label='Actions' tabIndex={0}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pascodeMetadata.map((pascode) => {
                    return (
                      <tr key={pascode.PASCODE}>
                        <td className='checkbox'>
                          <InputCheckbox
                            formClassName='form-horizontal font-0'
                            inputProps={{
                              id: 'all',
                              label: 'All',
                              name: 'Input Check',
                              type: 'checkbox',
                            }}
                          />
                        </td>
                        <td aria-label={`${pascode.ServicingBy} Support`} tabIndex={0}>
                          {`${pascode.ServicingBy} Support`}
                        </td>
                        <td aria-label={pascode.Installation} tabIndex={0}>
                          {pascode.Installation}
                        </td>
                        <td aria-label={pascode.Organization} tabIndex={0}>
                          {pascode.Organization}
                        </td>
                        <td aria-label={pascode.PASCODE} tabIndex={0}>
                          {pascode.PASCODE}
                        </td>
                        <td aria-label={pascode.AdditionalPASCODEsSupported} tabIndex={0}>
                          {pascode.AdditionalPASCODEsSupported}
                        </td>
                        <td aria-label={pascode.ServicingCSSPASCODE} tabIndex={0}>
                          {pascode.ServicingCSSPASCODE}
                        </td>
                        <td aria-label={pascode.modifiedBy} tabIndex={0}>
                          {pascode.ItemModifiedBy.properties.firstName}{' '}
                          {pascode.ItemModifiedBy.properties.lastName} |{' '}
                          {formatDate(pascode.ItemModified)}
                        </td>
                        <td aria-label={pascode.IsArchived ? 'Yes' : 'No'} tabIndex={0}>
                          {pascode.IsArchived}
                        </td>
                        <td tabIndex={0}>
                          <Buttons
                            aria-label='Edit'
                            className='btn-border btn-sm font-0 font-xl-14 btn-border-radius3 ms-auto color-primary text-nowrap sourcesansprosemibold'
                            icon='icon-pencil me-xl-1 font-10'
                            label='Edit'
                            onClick={() => {
                              setShowEdit(true)
                              onEdit({ ItemKey: pascode.PASCODE })
                              setShowAddPopup(false)
                            }}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default PascodeConfiguration
